import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { GuiasRelacionadas } from "@/components/GuiasRelacionadas";
import hero1280Asset from "../assets/hero-1280.webp.asset.json";
import hero1920Asset from "../assets/hero-1920.webp.asset.json";
import hero640Asset from "../assets/hero-640.webp.asset.json";

const srcSet = `${hero640Asset.url} 640w, ${hero1280Asset.url} 1280w, ${hero1920Asset.url} 1920w`;
const OG_IMAGE = `https://elpajardebenito.es${hero1280Asset.url}`;
const URL_CANON = "https://elpajardebenito.es/casa-rural-con-chimenea";

const faqs = [
  { q: "¿La chimenea se puede usar en cualquier época del año?", a: "Sí. La chimenea de leña del salón está operativa todo el año, aunque es especialmente agradable de octubre a mayo. La leña está incluida en la estancia." },
  { q: "¿Además de chimenea tiene calefacción?", a: "Sí, la casa dispone de calefacción central en todas las estancias y habitaciones, además de la chimenea de leña del salón." },
  { q: "¿Es una casa rural con chimenea cerca de Madrid?", a: "Sí, estamos a 1 h 30 min de Madrid por la A-2, en El Ordial (Sierra Norte de Guadalajara). Es una de las escapadas con chimenea más accesibles desde la capital." },
  { q: "¿Se puede reservar solo la habitación o la casa entera?", a: "Ambas opciones. Puedes reservar una habitación doble con baño o la casa completa hasta 14 personas, con el salón y la chimenea de uso compartido o exclusivo." },
];

export const Route = createFileRoute("/casa-rural-con-chimenea")({
  head: () => ({
    meta: [
      { title: "Casa rural con chimenea en Guadalajara · El Pajar de Benito" },
      { name: "description", content: "Casa rural con chimenea de leña y calefacción en la Sierra Norte de Guadalajara, a 1 h 30 de Madrid. Escapada de invierno para parejas y grupos." },
      { property: "og:title", content: "Casa rural con chimenea · El Pajar de Benito" },
      { property: "og:description", content: "Chimenea de leña, calefacción central y montaña. Escapada de invierno en la Sierra del Alto Rey." },
      { property: "og:url", content: URL_CANON },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL_CANON }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { label: "Inicio", to: "/" },
            { label: "Casa rural con chimenea", to: "/casa-rural-con-chimenea" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Casa rural con chimenea" }]} />
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img src={hero1280Asset.url} srcSet={srcSet} sizes="100vw" alt="Casa rural con chimenea de leña en la Sierra Norte de Guadalajara" width={1920} height={1280} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 mx-auto flex h-full max-w-5xl flex-col justify-end px-4 pb-16 md:px-6">
          <p className="text-xs uppercase tracking-[0.28em] text-white/80">Escapada de invierno</p>
          <h1 className="mt-3 font-serif text-4xl text-white md:text-5xl">Casa rural con chimenea</h1>
          <p className="mt-4 max-w-2xl text-white/90">Chimenea de leña, calefacción central y montaña en la Sierra del Alto Rey, a 1 h 30 min de Madrid.</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Invierno junto al fuego en la Sierra Norte</h2>
        <p className="mt-4 text-muted-foreground">
          Si buscas una <strong>casa rural con chimenea</strong> para desconectar, <strong>El Pajar de Benito</strong> tiene el salón que imaginas:
          chimenea de leña encendida, vigas de madera y vistas a la sierra. La leña está incluida y la casa cuenta además con
          <strong> calefacción central</strong> en todas las habitaciones, así que el frío de la sierra se queda fuera.
        </p>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {[
            { t: "Chimenea de leña", d: "En el salón principal, con leña incluida en la estancia." },
            { t: "Calefacción central", d: "Confort en todas las habitaciones y baños." },
            { t: "Salón amplio", d: "Espacio común para grupos, sobremesas y juegos de mesa." },
            { t: "Parejas o grupos", d: "Habitación doble con baño o casa completa hasta 14 plazas." },
            { t: "1 h 30 de Madrid", d: "Acceso directo por la A-2 y la CM-1101." },
            { t: "Nieve y otoño", d: "Alto Rey nevado en invierno, hayedos en otoño." },
          ].map((b) => (
            <div key={b.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg">{b.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-muted/40">
        <div className="mx-auto max-w-4xl px-4 py-16 md:px-6">
          <h2 className="font-serif text-3xl text-foreground">Planes para una escapada de invierno</h2>
          <ul className="mt-6 space-y-3 text-muted-foreground">
            <li><strong>Ruta al Alto Rey:</strong> subida al pico (1.858 m), a menudo con nieve entre diciembre y marzo.</li>
            <li><strong>Pueblos negros:</strong> Majaelrayo y Valverde de los Arroyos, especialmente bonitos con niebla.</li>
            <li><strong>Observación de estrellas:</strong> cielos muy oscuros; noches despejadas ideales para astronomía.</li>
            <li><strong>Gastronomía serrana:</strong> asados, migas y cabrito en los pueblos del entorno.</li>
          </ul>
          <div className="mt-8">
            <Link to="/blog" className="text-sm font-medium text-primary hover:underline">Leer el blog del entorno →</Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Preguntas frecuentes</h2>
        <div className="mt-8 space-y-6">
          {faqs.map((f) => (
            <div key={f.q}>
              <h3 className="font-medium text-foreground">{f.q}</h3>
              <p className="mt-2 text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <GuiasRelacionadas slugs={["estrellas-alto-rey", "guia-hayedo-tejera-negra", "que-ver-sierra-norte-guadalajara"]} />

      <section className="mx-auto max-w-3xl px-4 pb-24 text-center md:px-6">
        <h2 className="font-serif text-3xl text-foreground md:text-4xl">Reserva tu escapada con chimenea</h2>
        <p className="mt-4 text-muted-foreground">Disponibilidad en tiempo real y reserva directa sin comisiones.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/reserva" className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Ver disponibilidad</Link>
          <Link to="/casa-rural-guadalajara" className="rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-muted">Casa rural en Guadalajara</Link>
        </div>
      </section>
    </>
  );
}
